<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { usePageContext } from '@/composables/usePageContext'
import { httpPost } from '@/lib/http'
import { arbitrary, price } from '@/helpers/numberFormatters'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import SeminarIntensiveConditionsCard from '@/ui-kit/seminars/IntensiveConditionsCard.vue'
import type { PaymentModalExpose } from '~/ui-kit/components/PaymentModal/PaymentModal.vue'
import type { PhonePromptModalExpose } from '~/ui-kit/components/PhonePromptModal/PhonePromptModal.vue'

interface Discount {
  type: string
  reason: string
  percent: number
  remaining: number
  active: boolean
}

interface PriceData {
  base_price: number
  individual_price: number
  discounts: Discount[]
}

type PaymentOption = '100' | '50' | '25'
type PaymentOptions = Partial<Record<PaymentOption, number>>

interface PageContext {
  seminar_name: string
  needs_phone: boolean
  seminar_group_id: number
  seminar_description_url: string
  price: PriceData
  payment_options: PaymentOptions
  intensive_group_id?: number
  intensive_price?: PriceData
}

interface RequestChangePhoneResponse {
  failed: boolean
  message?: string
  errors?: {
    phone_number?: Array<{ message: string }>
  }
}

const ctx = usePageContext<PageContext>()
const { notify } = useNotification()

const paymentModal = useTemplateRef<PaymentModalExpose>('paymentModal')
const phonePrompt = useTemplateRef<PhonePromptModalExpose>('phonePrompt')

const hasIntensive = computed(() => Boolean(ctx.intensive_group_id && ctx.intensive_price))

const seminarDiscounts = computed(() => ctx.price.discounts.filter(discount => discount.active))

const intensiveDiscounts = computed(() => {
  if (!ctx.intensive_price) return []
  return ctx.intensive_price.discounts.filter(discount => discount.active)
})

const total = computed(() => {
  const seminar = ctx.price
  const intensive =
    hasIntensive.value && ctx.intensive_price ? ctx.intensive_price : { base_price: 0, individual_price: 0 }

  return {
    base_price: seminar.base_price + intensive.base_price,
    individual_price: seminar.individual_price + intensive.individual_price,
    discount_amount: seminar.base_price - seminar.individual_price + (intensive.base_price - intensive.individual_price)
  }
})

const paymentOptions = computed<PaymentOptions>(() => {
  const options: PaymentOptions = { ...ctx.payment_options }
  if (!hasIntensive.value || !ctx.intensive_price) return options

  const intensivePrice = ctx.intensive_price.individual_price
  for (const key of Object.keys(options) as PaymentOption[]) {
    const option = options[key]
    if (option === undefined) continue
    options[key] = option + intensivePrice
  }

  return options
})

function formatPrice(value: number | undefined) {
  return price.rub(value ?? 0)
}

function formatPercent(value: number) {
  return `-${arbitrary(value)}%`
}

async function setPhoneNumber() {
  const phone = await phonePrompt.value?.$prompt().catch(() => null)
  if (!phone) return false

  try {
    const response = await httpPost<RequestChangePhoneResponse>('/users/request_change_phone_number', {
      phone_number: phone
    })

    if (response.failed && response.errors) {
      throw response.errors.phone_number?.[0]?.message ?? response.message
    }

    if (response.failed) {
      throw response.message
    }

    return true
  } catch (error) {
    console.error(error)
    notify({
      title:
        typeof error === 'string'
          ? error
          : 'Произошла неизвестная ошибка при попытке установить номер телефона. Пожалуйста, повторите попытку позже.',
      type: 'error'
    })
    return false
  }
}

async function doPurchase(type: PaymentOption) {
  if (ctx.needs_phone) {
    const success = await setPhoneNumber()
    if (!success) return
  }

  const optionPrice = paymentOptions.value[type]
  if (optionPrice === undefined) return

  const name = hasIntensive.value ? `${ctx.seminar_name} + Интенсив` : ctx.seminar_name

  paymentModal.value?.open({
    enableCredit: type === '100',
    url: '/seminars/request_purchase_seminar',
    order: {
      name,
      price: optionPrice
    },
    query: {
      group_id: ctx.seminar_group_id,
      intensive_group_id: ctx.intensive_group_id ?? '',
      payment_type: type
    }
  })
}
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div v-cloak class="content purchase-seminar">
      <v-notifications position="bottom right" />
      <h1 class="header full-width flex-adaptive justify-space-between">Оплата семинара</h1>
      <v-payment-modal ref="paymentModal" />
      <v-phone-prompt-modal ref="phonePrompt" />

      <v-button mode="primary outlined" class="mt-2" :href="ctx.seminar_description_url">
        <v-icon class="mr-1">mdi-arrow-left</v-icon>
        Вернуться к описанию
      </v-button>

      <div class="purchase-seminar__container mt-4">
        <div class="d-flex">
          <div class="d-flex flex-spacer text-body-bold" v-text="ctx.seminar_name" />
          <div class="ml-1 d-flex" :class="{ 'purchase-seminar__price_old': seminarDiscounts.length }">
            {{ formatPrice(ctx.price.base_price) }}
          </div>
        </div>

        <div v-for="(discount, index) in seminarDiscounts" :key="discount.remaining" class="d-flex align-center mt-1">
          <div v-if="discount.type === 'season'" class="d-flex flex-column flex-spacer">
            <span>Скидка за раннюю запись</span>
            <span class="mt-half text-caption" v-text="discount.reason" />
          </div>
          <div v-else class="d-flex flex-spacer" v-text="discount.reason" />

          <div class="purchase-seminar__discount-badge red ml-1" style="margin-top: 2px">
            {{ formatPercent(discount.percent) }}
          </div>

          <div
            class="ml-1 d-flex"
            :class="{
              'purchase-seminar__price_old': index !== seminarDiscounts.length - 1,
              'purchase-seminar__price_final': index === seminarDiscounts.length - 1
            }"
          >
            {{ formatPrice(discount.remaining) }}
          </div>
        </div>

        <template v-if="hasIntensive">
          <div class="d-flex mt-2">
            <div class="d-flex flex-spacer text-body-bold">Интенсив</div>
            <div class="ml-1 d-flex" :class="{ 'purchase-seminar__price_old': intensiveDiscounts.length }">
              {{ formatPrice(ctx.intensive_price?.base_price) }}
            </div>
          </div>

          <div
            v-for="(discount, index) in intensiveDiscounts"
            :key="`i${discount.remaining}`"
            class="d-flex align-center mt-1"
          >
            <div v-if="discount.type === 'season'" class="d-flex flex-column flex-spacer">
              <span>Скидка за раннюю запись</span>
              <span class="mt-half text-caption" v-text="discount.reason" />
            </div>
            <div v-else class="d-flex flex-spacer" v-text="discount.reason" />

            <div class="purchase-seminar__discount-badge red ml-1" style="margin-top: 2px">
              {{ formatPercent(discount.percent) }}
            </div>

            <div
              class="ml-1 d-flex"
              :class="{
                'purchase-seminar__price_old': index !== intensiveDiscounts.length - 1,
                'purchase-seminar__price_final': index === intensiveDiscounts.length - 1
              }"
            >
              {{ formatPrice(discount.remaining) }}
            </div>
          </div>
        </template>

        <div class="d-flex justify-space-between text-title-bold mb-2 mt-4">
          <span>Итого:</span>
          {{ formatPrice(total.individual_price) }}
        </div>

        <template v-if="total.base_price !== total.individual_price">
          <div class="d-flex justify-space-between mb-1">
            Без скидки:
            <span class="text-body-bold">
              {{ formatPrice(total.base_price) }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            Скидка:
            <span class="text-body-bold red-text"> - {{ formatPrice(total.discount_amount) }} </span>
          </div>
        </template>
      </div>

      <SeminarIntensiveConditionsCard v-if="hasIntensive" class="mt-2" />

      <h2 class="title mt-5 mb-2">Выберите вариант оплаты</h2>
      <p v-if="hasIntensive" class="mb-3 mt-1"><b>Обратите внимание:</b> интенсив нельзя приобрести в рассрочку.</p>

      <div v-if="paymentOptions['100']" class="purchase-seminar__payment-option">
        <div class="flex-spacer text-overline">Полная оплата</div>

        <v-button mode="primary large filled" @click="doPurchase('100')">
          {{ formatPrice(paymentOptions['100']) }}
        </v-button>
      </div>

      <div v-if="paymentOptions['50']" class="purchase-seminar__payment-option">
        <div class="flex-spacer text-overline"><template v-if="hasIntensive">Интенсив + </template>Предоплата 50%</div>

        <v-button mode="primary large filled" @click="doPurchase('50')">
          {{ formatPrice(paymentOptions['50']) }}
        </v-button>
      </div>

      <div v-if="paymentOptions['25']" class="purchase-seminar__payment-option">
        <div class="flex-spacer text-overline"><template v-if="hasIntensive">Интенсив + </template>Предоплата 25%</div>

        <v-button mode="primary large filled" @click="doPurchase('25')">
          {{ formatPrice(paymentOptions['25']) }}
        </v-button>
      </div>

      <p class="mt-3 purchase-seminar__footer">
        При внесении предоплаты будут доступны организационные материалы. Обучающие материалы и лекции будут доступны
        после полной оплаты.
      </p>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/seminars/purchase_seminar.scss' as *;
</style>
